import type { CSSProperties } from 'react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { Dayjs } from 'dayjs'
import Debug from 'debug'
import throttle from 'lodash/throttle'
import type { UseInfiniteQueryResult } from '@tanstack/react-query'
import type { VirtualItem } from '@tanstack/react-virtual'
import { useVirtualizer } from '@tanstack/react-virtual'

import { usePhotoSubscription } from '@jotta/grpc-connect-client/useSubscription'
import { useElementSize, useElementSizeRef } from '@jotta/hooks'
import { useDelay } from '@jotta/hooks/useDelay'
import Spinner from '@jotta/ui/Spinner'
import type { BreakpointValue } from '@jotta/ui/useBreakpoint'
import { Breakpoint } from '@jotta/ui/useBreakpoint'
import { useBreakpointValue } from '@jotta/ui/useBreakpointIndex'
import { cn } from '@jotta/utils/css'
import { exhaustiveGuard } from '@jotta/utils/exhaustive'

import type { CollectionRef } from '../../../api/useCollection'
import type { SelectablePhoto } from '../../../store/SelectionStore'
import { NoTimelinePhotos } from '../../NoTimelinePhotos/NoTimelinePhotos'

import type { TimelineRowDataV2 } from './groupV2'
import { useGroupByDayAndRowV2 } from './groupV2'
import { PhotoRowV2, PhotoRowV2Divider, PhotoRowV2Heading } from './PhotoRowV2'
import { ScrubberV2 } from './ScrubberV2'
import type { TimelineType } from './useTimelineStream'
import {
  invalidateQueries,
  useCollectionRef,
  useTimelineQuery,
} from './useTimelineStream'
import {
  capturedDayFromNanos,
  nanosFromRow,
  timestampMillisToNanos,
} from './util'

const debug = Debug('timeline')

const DIVIDER_HEIGHT = 32
const FETCH_MORE_OFFSET = 3000 // Pixels

const RATIOS: Record<BreakpointValue, { columns: number; preserve: boolean }> =
  {
    base: { columns: 3, preserve: false },
    [Breakpoint.sm]: { columns: 3, preserve: false },
    [Breakpoint.md]: { columns: 4, preserve: false },
    [Breakpoint.lg]: { columns: 5, preserve: true },
    [Breakpoint.xl]: { columns: 5, preserve: true },
    [Breakpoint['2xl']]: { columns: 6, preserve: true },
    [Breakpoint['3xl']]: { columns: 7, preserve: true },
    [Breakpoint['4xl']]: { columns: 9, preserve: true },
  }

function useAspectRatio() {
  const breakpointValue = useBreakpointValue()
  return RATIOS[breakpointValue]
}

function useFetchMore(
  query: UseInfiniteQueryResult,
  container: HTMLElement | null,
) {
  useEffect(() => {
    if (!container) {
      return
    }

    const scrollHandler = throttle(() => {
      if (query.isFetching) {
        return
      }
      const { hasNextPage, fetchNextPage, hasPreviousPage, fetchPreviousPage } =
        query
      const { scrollHeight, clientHeight, scrollTop } = container

      const bottomOffset = scrollHeight - clientHeight - scrollTop
      if (bottomOffset < FETCH_MORE_OFFSET && hasNextPage) {
        debug(
          `fetching next page: bottomOffset=${bottomOffset}, scrollTop=${scrollTop}, scrollHeight=${scrollHeight}, clientHeight=${clientHeight}`,
        )
        fetchNextPage()
      } else if (scrollTop < FETCH_MORE_OFFSET && hasPreviousPage) {
        debug(`fetching previous page, scrollTop=${scrollTop}`)
        fetchPreviousPage()
      }
    }, 100)

    scrollHandler()
    scrollHandler.flush()
    container.addEventListener('scroll', scrollHandler)

    return () => {
      container.removeEventListener('scroll', scrollHandler)
      scrollHandler.cancel()
    }
  }, [container, query])
}

function usePreserveScroll(
  query: ReturnType<typeof useTimelineQuery>,
  container: HTMLDivElement | null,
) {
  const height = useRef(0)
  const isScrolling =
    useDelay({
      value: container?.scrollTop,
      delayMs: 50,
    }) !== container?.scrollTop

  // a pending fetch previous page triggers the need to restore scroll position after completion
  // but try to avoid the restoration from happening in the middle of active scrolling
  const scrollRestorationPending =
    query.isFetchingPreviousPage || (height.current && isScrolling)
  if (container && scrollRestorationPending) {
    height.current = container.scrollHeight + container.clientHeight
  }

  useEffect(() => {
    if (!container || !scrollRestorationPending) {
      return
    }

    return () => {
      // after some previous page has been fetched, we need to move the scroll position
      // forward to compensate for new elements added to the start of the data set
      // seen by the virtualizer
      const { scrollHeight, clientHeight, scrollTop } = container
      const newHeight = scrollHeight + clientHeight
      const oldHeight = height.current
      if (oldHeight > 0 && newHeight > oldHeight) {
        const diff = newHeight - height.current
        const newScrollTop = diff + scrollTop
        debug(
          `restoring scroll pos: ${scrollTop} -> ${newScrollTop}, newHeight=${newHeight} > height=${oldHeight}, diff=${diff}`,
        )
        container.scrollTop = newScrollTop
      }
      height.current = 0
    }
  }, [container, scrollRestorationPending])

  const queryRef = useRef(query)
  if (scrollRestorationPending) {
    // use the historical query state while scrolling to prevent jank
    return queryRef.current
  } else {
    queryRef.current = query
    return query
  }
}

function usePreserveItem(
  resizing: boolean,
  item: TimelineRowDataV2 | undefined,
  restoreTimestampNanos: (timestampNanos: bigint) => void,
) {
  const itemRef = useRef(item)
  const preserving = useRef(false)
  if (!resizing && !preserving.current) {
    itemRef.current = item
  } else if (resizing) {
    preserving.current = true
  }

  useEffect(() => {
    if (!resizing && preserving.current) {
      preserving.current = false
      const preservedItem = itemRef.current
      if (preservedItem) {
        debug(`restore preservedItem key=${preservedItem.key}`)
        restoreTimestampNanos(nanosFromRow(preservedItem))
      }
    }
  }, [resizing, restoreTimestampNanos])
}

function LoadingSection({ show = true }: { show?: boolean }) {
  const shouldDisplay =
    useDelay({
      delayMs: 200,
      value: show,
      initialValue: false,
    }) && show

  if (!shouldDisplay) {
    return null
  }
  return (
    <div className="flex h-20 w-full items-center justify-center">
      <Spinner size="medium" />
    </div>
  )
}

export function TimelineV2({
  type,
  onPhotos = () => {},
  navigateTo,
}: {
  type: TimelineType
  onPhotos?: (photos: SelectablePhoto[]) => void
  navigateTo?: bigint
}) {
  const collectionRef = useCollectionRef(type)
  const [timestampNanos, setTimestampNanos] = useState<bigint>(() =>
    timestampMillisToNanos(Date.now()),
  )
  const [isScrubbing, setIsScrubbing] = useState(false)

  usePhotoSubscription(invalidateQueries)

  const aspectRatio = useAspectRatio()
  const { columns, preserve: preserveAspect } = aspectRatio
  const scrollElementRef = useRef<HTMLDivElement>(null)
  const timelineElementRef = useRef<HTMLDivElement>(null)
  const containerElementRef = useRef<HTMLDivElement>(null)
  const { width: containerWidth, height: containerHeight } = useElementSize(
    containerElementRef.current,
  )
  const { width, height } = useElementSize(timelineElementRef.current)
  const rowHeight = width ? width / columns : 240

  const sizeMap: Record<TimelineRowDataV2['content'], number> = {
    row: rowHeight,
    header: 76,
    divider: DIVIDER_HEIGHT,
  }
  const previousContainerWidth = useDelay({
    delayMs: 100,
    value: containerWidth,
  })
  const changingWidth =
    previousContainerWidth !== 0 && previousContainerWidth !== containerWidth
  const changingAspectRatio =
    useDelay({
      delayMs: 100,
      value: aspectRatio,
    }) !== aspectRatio
  const resizing = changingWidth || changingAspectRatio

  const timelineQuery = useTimelineQuery(collectionRef, timestampNanos)

  const query = usePreserveScroll(timelineQuery, scrollElementRef.current)
  useFetchMore(query, scrollElementRef.current)

  const photos = useMemo(
    () => query.data?.pages.flatMap(value => value.photos) || [],
    [query.data],
  )

  const photoRows = useGroupByDayAndRowV2(columns, query.data, preserveAspect)
  const photoRowsRef = useRef(photoRows)
  photoRowsRef.current = photoRows

  useEffect(() => onPhotos(photos), [photos, onPhotos])

  const virtualizer = useVirtualizer({
    count: photoRows.length,
    getScrollElement: () => scrollElementRef.current,
    estimateSize: index => sizeMap[photoRows[index].content],
    overscan: 50,
    paddingEnd: 16,
    useAnimationFrameWithResizeObserver: true,
    getItemKey: i => photoRows[i].key,
    gap: 4,
  })

  const [stickyHeader, setStickyHeader] = useState<HTMLDivElement | null>(null)
  const stickyHeaderRectRef = useElementSizeRef(stickyHeader)

  const virtualItems = virtualizer.getVirtualItems()

  // Get date of top current visible row
  const { indicatorDate, centerRow } = useMemo(() => {
    if (!photoRows.length || !scrollElementRef.current) {
      return {}
    }

    const scrollTop = scrollElementRef.current.scrollTop
    const stickyHeaderHeight = stickyHeaderRectRef.current.height
    const visualScrollTop = Math.max(0, scrollTop + stickyHeaderHeight / 2)
    const firstCoveredItem = virtualItems.find(
      item => item.end > visualScrollTop,
    )
    if (!firstCoveredItem) {
      return {}
    }

    const centerOffset = scrollTop + containerHeight / 2
    const centerVirtualItem = virtualizer.getVirtualItemForOffset(centerOffset)
    const centerRow = centerVirtualItem
      ? photoRows.at(centerVirtualItem.index)
      : undefined

    const firstCoveredItemIndex = firstCoveredItem.index
    for (let i = firstCoveredItemIndex; i >= 0; i--) {
      const row = photoRows[i]
      if (row.content === 'header') {
        return {
          indicatorDate: row,
          centerRow,
        }
      }
    }
    return {}
  }, [
    photoRows,
    stickyHeaderRectRef,
    virtualItems,
    containerHeight,
    virtualizer,
  ])

  // Request navigation through prop, carousel or scrubber
  const navigateToNanos = useCallback(
    (navigateToNanos: bigint, align: 'start' | 'center') => {
      const navigateToDay = capturedDayFromNanos(navigateToNanos)
      const photoRows = photoRowsRef.current

      const exactRowIndex = photoRows.findIndex(
        row =>
          row.content === 'row' &&
          row.elements.some(p => p.timestampNanos === navigateToNanos),
      )
      const dayRowIndex = photoRows.findIndex(
        row => row.content === 'header' && row.day === navigateToDay,
      )
      // If we have the photo already, scroll to it, otherwise load from that timestamp
      if (exactRowIndex >= 0) {
        debug(
          `scrolling to exact index=${exactRowIndex}, nanos=${navigateToNanos}, align=${align}`,
        )
        virtualizer.scrollToIndex(exactRowIndex, {
          align: align,
        })
      } else if (dayRowIndex >= 0) {
        debug(
          `scrolling to day index=${dayRowIndex}, nanos=${navigateToNanos}, day=${navigateToDay}, align=${align}`,
        )
        virtualizer.scrollToIndex(dayRowIndex, {
          align: align,
        })
      } else {
        debug(`navigate to ${navigateToNanos}`)
        setTimestampNanos(navigateToNanos)
      }
    },
    [virtualizer],
  )

  useEffect(() => {
    if (navigateTo) {
      navigateToNanos(navigateTo, 'center')
    }
  }, [navigateTo, navigateToNanos])

  const handleSelect = useCallback(
    (dayjs: Dayjs) => {
      const startOfDay = dayjs.startOf('day')
      const ts = timestampMillisToNanos(dayjs.valueOf())
      debug(`handleSelect: request=${dayjs}, selecting=${startOfDay}, ts=${ts}`)
      navigateToNanos(ts, 'start')
    },
    [navigateToNanos],
  )

  const centerTimestampNanos = useCallback(
    (timestampNanos: bigint) => navigateToNanos(timestampNanos, 'center'),
    [navigateToNanos],
  )
  usePreserveItem(resizing, centerRow, centerTimestampNanos)

  const stickyDay = indicatorDate

  if (query.data && photoRows.length === 0) {
    return <NoTimelinePhotos type={type} />
  }

  return (
    <div
      ref={containerElementRef}
      className="absolute grid size-full grid-cols-[1fr_min-content] gap-0 overflow-hidden"
    >
      <div
        className="h-full overflow-y-auto px-1 scrollbar-none md:px-4"
        ref={scrollElementRef}
      >
        {stickyDay && (
          <div
            className="absolute top-0 z-stickyTimelineHeader w-full"
            ref={setStickyHeader}
          >
            <PhotoRowV2Heading
              collectionRef={collectionRef}
              header={stickyDay}
            />
            {/*
             quite hard to make the blur not cause visual issues with rounded corners in different browser, so test this properly.
             mix-blend-overlay is a workaround for a chromium rendering bug https://issues.chromium.org/issues/40073287
             */}
            <div className="absolute inset-0 -z-10 bg-white/85 mix-blend-overlay backdrop-blur-[12px]" />
          </div>
        )}

        <div
          ref={timelineElementRef}
          className={cn('flex-auto select-none transition-all duration-200', {
            'blur-[2px]': isScrubbing,
            'opacity-75': isScrubbing,
          })}
          id="timeline"
        >
          <div
            className="relative min-h-24"
            style={{ height: virtualizer.getTotalSize() }}
          >
            <div className="absolute top-0 w-full">
              <LoadingSection show={query.isFetchingPreviousPage} />
            </div>

            <div className="absolute left-0 top-0 w-full" key={timestampNanos}>
              {virtualItems.map(item => (
                <VirtualRow
                  key={item.key}
                  collectionRef={collectionRef}
                  item={item}
                  rows={photoRows}
                  columns={columns}
                />
              ))}
            </div>

            <div className="absolute bottom-0 w-full">
              <LoadingSection
                show={query.isFetchingNextPage || query.isPending}
              />
            </div>
          </div>
        </div>
      </div>

      <ScrubberV2
        collectionRef={collectionRef}
        indicatorDate={indicatorDate?.day}
        scrollingElement={scrollElementRef.current}
        onSelect={handleSelect}
        onScrubbing={setIsScrubbing}
      />
    </div>
  )
}

function VirtualRow({
  collectionRef,
  item,
  rows,
  columns,
}: {
  collectionRef: CollectionRef
  item: VirtualItem
  rows: TimelineRowDataV2[]
  columns: number
}) {
  const { index, start } = item
  const row = rows[index]
  const content = row.content

  const style: CSSProperties = {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    transform: `translateY(${start}px)`,
  }

  switch (content) {
    case 'header': {
      return (
        <PhotoRowV2Heading
          collectionRef={collectionRef}
          header={row}
          style={style}
        />
      )
    }
    case 'divider': {
      style.height = `${DIVIDER_HEIGHT}px`
      return <PhotoRowV2Divider style={style} row={row} />
    }

    case 'row': {
      style.aspectRatio = `${columns} / 1`
      return (
        <PhotoRowV2
          row={row}
          next={rows.at(index + 1)}
          style={style}
          showOwnerAvatar={false}
          showSimilarSearch={false}
        />
      )
    }

    default: {
      return exhaustiveGuard(content)
    }
  }
}
